import { isDefined } from '@sgme/fp';
import { UnderlyingTag } from 'components/underlying-tag';
import { useUnderlyingContext } from 'hooks/useUnderlyingContext';
import type { ForexUnderlying } from 'models/underlying/forex';
import { FC, useMemo } from 'react';

import { ActionCell, CodeCell, CutOffCell, NameCell, WeightCell } from '../../Common/Cells';
import { Row } from '../../Common/Row';
import { Table } from '../../Common/Table';

export interface ForexTableProps {
  underlyings: ForexUnderlying[];
}

export const ForexTable: FC<ForexTableProps> = ({ underlyings }) => {
  const { canBeRemoved, isReadOnly, hasCutOff } = useUnderlyingContext();
  const showWeight = useMemo(() => {
    return underlyings.some(x => isDefined(x.weight));
  }, [underlyings]);

  const showCutOff = useMemo(() => {
    return hasCutOff ?? false;
  }, [hasCutOff]);

  return (
    <Table>
      <thead>
        <tr>
          <CodeCell.Header type={'Name'} />
          <CutOffCell.Header showCutOff={showCutOff} />
          <WeightCell.Header showWeight={showWeight} />
          <ActionCell.RemoveAll />
        </tr>
      </thead>
      <tbody>
        {underlyings.map(underlying => (
          <Row key={underlying.id}>
            <NameCell.Data underlying={underlying} shouldDisplayCode>
              <UnderlyingTag value={underlying} label={underlying.name} size="lg" isReadOnly />
            </NameCell.Data>
            <CutOffCell.Data
              showCutOff={showCutOff}
              underlyingId={underlying.id}
              cutOff={underlying.cutOff}
              cutOffs={underlying.cutOffs}
              defaultCutOff={underlying.defaultCutOff}
            />
            <WeightCell.Data showWeight={showWeight} weightValue={underlying.weight} underlyingId={underlying.id} />

            {canBeRemoved && !isReadOnly && <ActionCell.Remove underlyingId={underlying.id} />}
          </Row>
        ))}
      </tbody>
    </Table>
  );
};

ForexTable.displayName = 'ForexTable';
